import { SurveyQuestionComponent } from '@chiroup/components/survey/SurveyQuestionComponent';
import React from 'react';
import {
  SoftCheckboxQuestion,
  SoftDateQuestion,
  SoftInputQuestion,
  SoftRadioQuestion,
  SoftSliderQuestion,
  SoftTextareaQuestion,
} from './questions';
import { SoftGroupQuestion } from './SoftGroupQuestion';
import type { SoftQuestionProps, SoftQuestionRenderers } from './types';

const DEFAULT_RENDERERS: Required<SoftQuestionRenderers> = {
  radio: SoftRadioQuestion,
  checkbox: SoftCheckboxQuestion,
  input: SoftInputQuestion,
  textarea: SoftTextareaQuestion,
  slider: SoftSliderQuestion,
  date: SoftDateQuestion,
  group: SoftGroupQuestion,
};

// Bodypicker / picture / video etc. — keep the legacy shared
// rendering. Those are composite kinds that aren't worth re-skinning
// in the Soft Card system.
const renderLegacy = (props: SoftQuestionProps) => (
  <SurveyQuestionComponent
    show={!props.hidden}
    question={props.question.question}
    type={props.question.type}
    typeOptions={props.question.typeOptions}
    options={props.question.options}
    id={props.question.id}
    onSelect={props.onSelect}
    disabled={props.disabled}
    images={props.question.images}
    showPrevious={props.showPrevious}
    goBack={props.onBack}
    progress={props.progress}
    language={props.language}
    primaryColor={props.primaryColor}
  />
);

// Dispatcher — picks the renderer for the current question's type.
// Pass `renderers` to override one or more types without touching the
// rest. Example:
//
//     <SoftSurveyQuestion {...props} renderers={{ slider: MyBigSlider }} />
//
// Falls back to the legacy shared `SurveyQuestionComponent` for any
// type the renderers map doesn't cover (bodypicker, picture, video,
// etc.).
export const SoftSurveyQuestion: React.FC<
  SoftQuestionProps & { renderers?: SoftQuestionRenderers }
> = ({ renderers, ...props }) => {
  const { type } = props.question;
  const merged = { ...DEFAULT_RENDERERS, ...(renderers ?? {}) };
  const Component = (merged as Record<string, React.FC<SoftQuestionProps> | undefined>)[type];

  if (Component) return <Component {...props} />;

  return renderLegacy(props);
};
